import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon, TransferOutIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash/fp';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import CreateReportModal from 'dashboard/components/CreateReportModal';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import DashboardTooltip from 'dashboard/components/Tooltip';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { optionWithSubtextMapper } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { FormCreateReportData } from 'dashboard/forms/FormCreateReport';
import { FormCsvUploadCreateReportData } from 'dashboard/forms/FormCsvUploadCreateReport';
import { Backdrop, Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { ReportFilterCondition } from 'dashboard/lib/reports/filterMetas';
import { Environment, EnvironmentStatus } from 'dashboard/models/environment';
import { ExporterFileType } from 'dashboard/models/exporter';
import { Report, ReportMatchResult, ReportStatus } from 'dashboard/models/report';
import { ReportTemplate, ReportTemplateFilterOption } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { ReportsFilterParams } from 'dashboard/redux/actions/reports';
import ReportAdvancedFilter from 'dashboard/views/DashboardReportsView/ReportAdvancedFilter';
import ReportsTable, {
  ReportsTableColumns,
} from 'dashboard/views/DashboardReportsView/ReportsTable';
import ReportsTableConfigurationDropdown from 'dashboard/views/DashboardReportsView/ReportsTableConfigurationDropdown';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

export const MAX_EXPORT_COUNT_LIMIT = 100_000;

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Pending',
    value: ReportStatus.Pending,
  },
  {
    label: 'Ready',
    value: ReportStatus.Ready,
  },
  {
    label: 'Errored',
    value: ReportStatus.Errored,
  },
  {
    label: 'Matched',
    value: ReportMatchResult.Matched,
  },
  {
    label: 'No Matches',
    value: ReportMatchResult.NoMatches,
  },
];

const LinkTooltip = styled(DashboardTooltip)`
  max-width: 256px;
`;

const NoTemplatesMessage = styled.div`
  background-color: ${({ theme }) => theme.color.background.highlight};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: ${BASE * 2.5}px;
  margin: ${Spacing[5]} ${Spacing[5]} 0 ${Spacing[5]};
`;

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: ${Spacing[4]};
`;

/**
 * Special styling for use with new table filters UI
 */
const SegmentedReportAdvancedFilter = styled(ReportAdvancedFilter)`
  > div {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: -1px;
  }
`;

export interface DashboardReportsViewProps {
  currentEnvironment: Environment;
  exportersProductFeatureIsDisabled: boolean;
  isCreateReportButtonDisabled: boolean;
  reports: Report[];
  reportTemplates: ReportTemplate[];
  reportTemplateFilterOptions: ReportTemplateFilterOption[];
  tags: Tag[];
  selectedReportTemplateFilterOption: ReportTemplateFilterOption;
  loadingReports: boolean;
  loadingReportTemplates: boolean;
  loadingTags: boolean;
  paginationParams: PaginationParams;
  sortParams: SortParams;
  filters: ReportsFilterParams;
  pageSize: number;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleExportData: (exporterFileType: ExporterFileType) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleCreateReport: (
    data: FormCreateReportData,
    reportTemplateFilterOption: ReportTemplateFilterOption,
  ) => Promise<void>;
  handleCsvUploadCreateReports: (
    data: FormCsvUploadCreateReportData,
    reportTemplateFilterOption: ReportTemplateFilterOption,
  ) => Promise<void>;
  handleReportTemplateFilterOptionSelect: (reportTemplateId?: string) => void;
  handleStatusFilterUpdate: (status: ReportStatus[], matchResult: ReportMatchResult[]) => void;
  handleReportTemplateIdFilterUpdate: (reportTemplateIds: string[]) => void;
  handleTagIdFilterUpdate: (tagIds: string[]) => void;
  handleAdvancedFilterUpdate: (conditions: ReportFilterCondition[][]) => void;
}

const DashboardReportsView = ({
  currentEnvironment,
  exportersProductFeatureIsDisabled,
  isCreateReportButtonDisabled,
  reports,
  reportTemplates,
  reportTemplateFilterOptions,
  tags,
  selectedReportTemplateFilterOption,
  loadingReports,
  loadingReportTemplates,
  loadingTags,
  paginationParams,
  sortParams,
  filters,
  pageSize,
  handlePageLinkClick,
  handlePageSizeClick,
  handleOmniSearch,
  handleDateUpdate,
  handleExportData,
  handleSortUpdate,
  handleCreateReport,
  handleCsvUploadCreateReports,
  handleReportTemplateFilterOptionSelect,
  handleStatusFilterUpdate,
  handleReportTemplateIdFilterUpdate,
  handleTagIdFilterUpdate,
  handleAdvancedFilterUpdate,
}: DashboardReportsViewProps) => {
  const [showCreateReportModal, { enable: openCreateReportModal, disable: hideCreateReportModal }] =
    useToggle(false);

  const [
    showExportTooLargeModal,
    { enable: openExportTooLargeModal, disable: hideExportTooLargeModal },
  ] = useToggle();

  const isCurrentEnvironmentActive =
    currentEnvironment.attributes.status === EnvironmentStatus.Active;

  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateReportModalParam = queryParams.get('openCreateReportModal');

    if (
      openCreateReportModalParam === 'true' &&
      !(!isCurrentEnvironmentActive || isCreateReportButtonDisabled || reportTemplates.length === 0)
    ) {
      openCreateReportModal();
    }
  }, [location, isCurrentEnvironmentActive, isCreateReportButtonDisabled, reportTemplates]);

  const {
    createdAtStart,
    createdAtEnd,
    status,
    reportTemplateIds,
    tagIds,
    omni,
    conditions,
    matchResult,
  } = filters;

  const [visibleColumns, setVisibleColumns] = useLocalStorage(
    LocalStorageKey.DashboardReportsTableVisibleColumns,
    [
      ReportsTableColumns.PrimaryInput,
      ReportsTableColumns.Status,
      ReportsTableColumns.ReportId,
      ReportsTableColumns.CreatedAt,
    ],
  );

  const toggleColumns = (columnName: ReportsTableColumns) => {
    if (visibleColumns.includes(columnName)) {
      setVisibleColumns(visibleColumns.filter((col) => col !== columnName));
    } else {
      setVisibleColumns([...visibleColumns, columnName]);
    }
  };

  const totalReportsCount = paginationParams?.['total-count'] ?? 0;

  const onHandleExportClick = (fileType: ExporterFileType) => {
    if (totalReportsCount >= MAX_EXPORT_COUNT_LIMIT) {
      openExportTooLargeModal();
    } else {
      handleExportData(fileType);
    }
  };

  const hasReportTemplateViewPermission = usePermission(Permission.ReportTemplateView);

  const reportTemplateIdFilterOptions = reportTemplateFilterOptions.map(({ name, id }) => {
    return { label: `${name} (${id})`, customLabel: optionWithSubtextMapper(name, id), value: id };
  });

  const reportTemplateIdFilterOptionsWithType = reportTemplateFilterOptions.map((filterOption) => {
    return { label: filterOption.name, value: filterOption.id, type: filterOption.type };
  });

  const tagIdFilterOptions = tags.map((tag) => {
    return { label: tag.attributes.name, value: tag.id };
  });

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'statuses', {
    emptyIsAll: true,
  });

  const reportTemplateIdFilterLabelTextMapper = useLabelTextMapper(
    reportTemplateIdFilterOptions,
    'report templates',
    { emptyIsAll: true },
  );

  const tagIdFilterLabelTextMapper = useLabelTextMapper(tagIdFilterOptions, 'tags', {
    emptyIsAll: true,
  });

  if (loadingReportTemplates || loadingTags) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  }

  const hasUsedFilters = !Object.values(filters).every(isEmpty);

  const filterConfig: FilterConfig[] = [
    {
      id: 'dateCreated',
      name: 'Date created',
      type: 'daterange',
      onChange: handleDateUpdate,
      value: {
        startDate: createdAtStart,
        endDate: createdAtEnd,
      },
    },
    {
      id: 'reportTemplate',
      htmlId: 'reports-view__template',
      name: 'Report template',
      type: 'multiselect-legacy',
      labelTextMapper: reportTemplateIdFilterLabelTextMapper,
      selectOptionValues: reportTemplateIdFilterOptions,
      onChange: handleReportTemplateIdFilterUpdate,
      value: reportTemplateIds || [],
    },
    {
      id: 'advanced',
      htmlId: 'reports-view__filter-criteria',
      name: 'Filter criteria',
      type: 'custom',
      filterElement: (
        <SegmentedReportAdvancedFilter
          values={conditions}
          templateOptions={reportTemplateIdFilterOptionsWithType}
          onChange={handleAdvancedFilterUpdate}
          size='md'
        />
      ),
      onDelete: () => handleAdvancedFilterUpdate([]),
      hasValue: conditions && conditions.length > 0,
    },
    {
      id: 'status',
      htmlId: 'reports-view__status',
      name: 'Status',
      type: 'multiselect-legacy',
      selectOptionValues: STATUS_FILTER_OPTIONS,
      labelTextMapper: statusFilterLabelTextMapper,
      onChange: (value: (ReportStatus | ReportMatchResult)[]) => {
        handleStatusFilterUpdate(
          value.filter((val) =>
            Object.values(ReportStatus)
              .map((x) => x.toString())
              .includes(val),
          ) as ReportStatus[],
          value.filter((val) =>
            Object.values(ReportMatchResult)
              .map((x) => x.toString())
              .includes(val),
          ) as ReportMatchResult[],
        );
      },
      value: [...(status ?? []), ...(matchResult ?? [])],
    },
    {
      id: 'tag',
      htmlId: 'reports-view__tags',
      name: 'Tag',
      type: 'multiselect-legacy',
      enabled: tagIdFilterOptions.length > 0,
      selectOptionValues: tagIdFilterOptions,
      labelTextMapper: tagIdFilterLabelTextMapper,
      onChange: handleTagIdFilterUpdate,
      searchEnabled: true,
      value: tagIds || [],
    },
  ];

  return (
    <>
      <ModalWrapper isOpen={showCreateReportModal} handleCloseModal={hideCreateReportModal}>
        <CreateReportModal
          reportTemplateFilterOptions={reportTemplateFilterOptions}
          selectedReportTemplateFilterOption={selectedReportTemplateFilterOption}
          handleClose={() => {
            hideCreateReportModal();
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handleReportTemplateFilterOptionSelect(null);
          }}
          handleCreateReport={async (data, reportTemplateFilterOption) => {
            await handleCreateReport(data, reportTemplateFilterOption);
            hideCreateReportModal();
          }}
          handleCsvUploadCreateReports={(
            data: FormCsvUploadCreateReportData,
            reportTemplateFilterOption: ReportTemplateFilterOption,
          ) => {
            const handleValue = handleCsvUploadCreateReports(data, reportTemplateFilterOption);
            hideCreateReportModal();
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handleReportTemplateFilterOptionSelect(null);
            return handleValue;
          }}
          handleReportTemplateFilterOptionSelect={handleReportTemplateFilterOptionSelect}
          hideReferenceId={false}
          showCsvUploadTab={true}
        />
      </ModalWrapper>

      <ModalWrapper isOpen={showExportTooLargeModal} handleCloseModal={hideExportTooLargeModal}>
        <ModalContainer>
          <ModalHeader onClose={hideExportTooLargeModal}>Export size limit exceeded</ModalHeader>

          <ModalBody>
            <Paragraph>
              You are attempting to export <strong>{MAX_EXPORT_COUNT_LIMIT} or more </strong>
              reports. This is greater than the allowable number of objects to export. Please change
              the filters and try again.
            </Paragraph>

            <Spacer height={4} />

            <Button
              width='full'
              autoFocus={true}
              id='confirmation-modal__cancel'
              variant='basic'
              onClick={hideExportTooLargeModal}
            >
              Revise Filters
            </Button>
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>

      <InactiveEnvironmentBanner />
      <Container>
        <Header title='Reports'>
          <LinkTooltip
            tooltip={
              isCreateReportButtonDisabled
                ? 'Permissions required to enable this feature.'
                : 'Report Template required to enable this feature.'
            }
            // This disables the tooltip, not the button.
            disabled={!isCreateReportButtonDisabled}
            placement='left-start'
            hideArrow
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [-7, 8],
                },
              },
            ]}
          >
            <Button
              id='reports__create-report-button'
              data-test='reports__create-report-button'
              onClick={openCreateReportModal}
              prefixIcon={<PlusIcon />}
              width='auto'
              disabled={
                !isCurrentEnvironmentActive ||
                isCreateReportButtonDisabled ||
                reportTemplateFilterOptions.length === 0
              }
            >
              Create report
            </Button>
            {!exportersProductFeatureIsDisabled && (
              <Permissioned permission={Permission.ReportExport}>
                <DropdownMenu
                  trigger={
                    <Button
                      id='reports__export'
                      variant='basic'
                      disabled={totalReportsCount === 0}
                      prefixIcon={<TransferOutIcon />}
                    >
                      Export
                    </Button>
                  }
                  disabled={totalReportsCount === 0}
                  align='end'
                >
                  {Object.values(ExporterFileType).map((fileType) => (
                    <DropdownMenu.Item
                      id={`reports__export__${fileType}`}
                      onClick={() => {
                        onHandleExportClick(fileType);
                      }}
                      key={`reports__export__${fileType}`}
                    >
                      {`To ${fileType}`}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu>
              </Permissioned>
            )}
          </LinkTooltip>
        </Header>
        <Section>
          <Card>
            <Card.Body>
              <TableTitleContainer>
                <Card.Title>Reports</Card.Title>
              </TableTitleContainer>
              {hasReportTemplateViewPermission && reportTemplates.length === 0 && (
                <NoTemplatesMessage>
                  <p>
                    To get started with reports, your organization needs to have active Report
                    Templates.
                  </p>
                  <Link to='/dashboard/contact-us'>
                    Please contact us to add a new Report Template.
                  </Link>
                </NoTemplatesMessage>
              )}

              <TableControls
                searchProps={{
                  handleInput: handleOmniSearch,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value: omni,
                  placeholder: 'Search by report ID, reference ID, or input',
                }}
                filters={filterConfig}
                propertiesButton={
                  <ReportsTableConfigurationDropdown
                    visibleColumns={visibleColumns}
                    toggleFunction={toggleColumns}
                  />
                }
              />
              <ReportsTable
                reports={reports}
                sortParams={sortParams}
                isLoading={loadingReports}
                hasUsedFilters={hasUsedFilters}
                tableLinkPrefix='/dashboard/reports'
                handleSortUpdate={handleSortUpdate}
                visibleColumns={visibleColumns}
              />
              {reports.length > 0 && (
                <Pagination
                  pageSize={pageSize}
                  paginationParams={paginationParams}
                  handlePageSizeClick={handlePageSizeClick}
                  handlePageLinkClick={handlePageLinkClick}
                />
              )}
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardReportsView;
